'use client';

import { useUser } from '@/features/auth/model/auth.selectors';
import type { AccountAttention } from '@/types/profile';

/**
 * Severity of the dot to draw on the Account tab/menu icon. Only one dot is
 * ever shown — `'alert'` (deletion scheduled) wins over `'warning'`
 * (no subscription or low tokens). Mirrors the Apple/Android behavior.
 */
export type AccountIssueSeverity = 'warning' | 'alert';

/**
 * Server-driven flag for the token balance row/readout color (show-negative,
 * 2026-06-01): `alert` (deep clawback debt) → red "you owe", `warning`
 * (running low / depleted-but-grace) → amber "top up". A grace overdraft is
 * shown as 0 and forgiven on top-up, so it stays amber, never red; only a
 * clawback debt (balance < tokens.min_balance) earns the red. The server owns
 * the threshold; the client only renders.
 */
export type TokenBalanceFlag = 'warning' | 'alert' | null | undefined;

/**
 * Maps the server-driven `account_attention.rows.tokens` flag to a text color
 * class for the token balance number. `alert` (clawback debt) → red, `warning`
 * (running low) → amber, null → empty so the caller keeps its own default. The
 * server owns the thresholds; the client only colors. Mirrors Apple/Android.
 */
export function tokenBalanceColorClass(tokensFlag: TokenBalanceFlag): string {
  if (tokensFlag === 'alert') return 'text-red-500';
  if (tokensFlag === 'warning') return 'text-amber-500';
  return '';
}

export interface AccountIssueState {
  /** Account is scheduled for deletion. */
  isScheduledForDeletion: boolean;
  /** User has no active subscription (guest mode). */
  hasNoSubscription: boolean;
  /** Subscribed user with token balance below the server-driven threshold. */
  hasLowTokens: boolean;
  /** Single severity for the Account tab/menu dot, or null if no issue. */
  severity: AccountIssueSeverity | null;
  /**
   * Per-row warning dots for the Account page (Plan / Tokens rows). Driven
   * by `account_attention.rows` — the server decides which rows light up,
   * the client only renders.
   */
  attentionRows: AccountAttention['rows'] | undefined;
}

/**
 * All Account-page attention indicators (single tab/menu dot + per-row
 * Plan/Tokens dots) are read directly off the server-computed
 * `user.account_attention` payload. The server owns the priority rules
 * (deletion > no_subscription > low_tokens) and the low-tokens threshold;
 * the client never re-derives them. See
 * `apps/api/app/Services/UserProfileService::computeAccountAttention`.
 */
export function useAccountIssue(): AccountIssueState {
  const user = useUser();
  const attention = user?.account_attention;

  return {
    isScheduledForDeletion: attention?.reason === 'scheduled_for_deletion',
    hasNoSubscription: attention?.reason === 'no_subscription',
    hasLowTokens: attention?.reason === 'low_tokens',
    severity: attention?.severity ?? null,
    attentionRows: attention?.rows,
  };
}
